import axios, { HttpStatusCode } from 'axios';
import { useCallback, useState } from 'react';

import { logger } from '@/services/logger';
import { restUrl } from '@/services/utils';

import { usePublicAsset } from './usePublicAsset';

interface UploaderProps {
    file: File;
    asPublicAsset?: boolean;
    /** Target folder path (no leading slash), same as legacy Angular uploader. */
    folderPath?: string;
}

const MAX_DIRECT_UPLOAD_BYTES = 32 * 1024 * 1024;

// Dedicated axios instance for the cross-origin PUT to a Google Cloud
// Storage signed URL. Does NOT inherit the app-wide request interceptors
// (e.g. the `Accept-Language` header set in
// `components/datamodel/queries.ts`) — GCS only expects the
// CORS-safelisted headers plus `Content-Type`, and any extra header would
// either be echoed into `Access-Control-Request-Headers` on the preflight
// (which the bucket rejects) or ignored on the wire.
const gcsUpload = axios.create();

const useAssetFolderUploader = () => {

    const [abortController, setAbortController] = useState(new AbortController());
    const { convertToPublicAsset } = usePublicAsset();

    const uploadFile = useCallback(async ({ file, asPublicAsset = true, folderPath = '' }: UploaderProps): Promise<string> => {

        // Abort any previous upload requests
        abortController.abort();

        // Track the next request in case we need to abort later on
        const newAbortController = new AbortController();
        setAbortController(newAbortController);

        const pathSeg = folderPath ? `/${encodeURI(folderPath)}` : '';
        const baseUpload = restUrl('assetFolders/_uploadFile');
        const uploadEndpoint = folderPath ? `${baseUpload}${pathSeg}` : baseUpload;

        const runLargeUpload = async () => {
            const contentType = file.type || 'application/octet-stream';
            const uploadUrlApi =
                `${restUrl(`assetFolders/_uploadLargeFile/uploadurl${pathSeg}`)}` +
                `?filename=${encodeURIComponent(file.name)}` +
                `&contentType=${encodeURIComponent(contentType)}`;
            const urlResponse = await axios.post(uploadUrlApi, null, {
                signal: newAbortController.signal
            });
            const { uploadUrl, assetKey } = urlResponse.data as { uploadUrl: string; assetKey: string };
            if (!uploadUrl || !assetKey) {
                throw new Error('Missing uploadUrl or assetKey');
            }
            await gcsUpload.put(uploadUrl, file, {
                headers: { 'Content-Type': contentType },
                signal: newAbortController.signal
            });
            const completeUrl =
                `${restUrl(`assetFolders/_uploadLargeFile/complete${pathSeg}`)}` +
                `?assetKey=${encodeURIComponent(assetKey)}`;
            return axios.post(completeUrl, null, { signal: newAbortController.signal });
        };

        const response =
            file.size >= MAX_DIRECT_UPLOAD_BYTES
                ? await runLargeUpload()
                : await axios.postForm(
                    uploadEndpoint,
                    { file },
                    { signal: newAbortController.signal }
                );

        if (response.status === HttpStatusCode.Ok) {

            if (asPublicAsset) {
                return await convertToPublicAsset(response.data.path);
            } else {
                return response.data.privateAssetUrl;
            }

        }

        logger.error('useAssetFolderUploader: Could not upload asset', response);
        throw new Error();

        // We only need one instance of this function.
        // eslint-disable-next-line
    }, []);

    return { uploadFile };

};

export { useAssetFolderUploader };
